import { GetRfidBadgeOrdersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/rfid-badge-orders/get-rfid-badge-orders-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { FulfillRfidBadgeOrderModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/inputs/rfid-badge-orders/fulfill-rfid-badge-order.model';
import { RfidBadgeOrderModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/outputs/rfid-badge-orders/rfid-badge-order.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  EMSP_BO_RFID_BADGE_ORDER_FILTERS_LSK,
  EMSP_BO_RFID_BADGE_ORDER_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  EMSP_BO_RFID_BADGE_ORDER_LIST_DEFAULT_OPTIONS,
  EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER_NAME_LIST,
  EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_FIELD,
  EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_ORDER,
  EmspBoRfidBadgeOrderListOptionsModel,
} from './emsp-bo-rfid-badge-order-list/emsp-bo-rfid-badge-order-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoRfidBadgeOrderContext =
  createContext<RfidBadgeOrderModel | null>(null);

export const useEmspBoRfidBadgeOrderContext = (): RfidBadgeOrderModel => {
  const context = useContext(EmspBoRfidBadgeOrderContext);

  if (!context) {
    throw new Error(
      'useEmspBoRfidBadgeOrderContext must be used within EmspBoRfidBadgeOrderContext.Provider',
    );
  }

  return context;
};

interface EmspBoRfidBadgeOrderContextProviderProps {
  data: RfidBadgeOrderModel;
  children: React.ReactNode;
}

export const EmspBoRfidBadgeOrderContextProvider = ({
  data,
  children,
}: EmspBoRfidBadgeOrderContextProviderProps): React.JSX.Element => (
  <EmspBoRfidBadgeOrderContext.Provider value={data}>
    {children}
  </EmspBoRfidBadgeOrderContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const FulfillRfidBadgeOrderContext =
  createContext<DeepPartialSkipArrayKey<FulfillRfidBadgeOrderModel> | null>(
    null,
  );

export const useFulfillRfidBadgeOrderContext =
  (): DeepPartialSkipArrayKey<FulfillRfidBadgeOrderModel> => {
    const context = useContext(FulfillRfidBadgeOrderContext);

    if (!context) {
      throw new Error(
        'useFulfillRfidBadgeOrderContext must be used within FulfillRfidBadgeOrderContext.Provider',
      );
    }

    return context;
  };

interface FulfillRfidBadgeOrderContextProviderProps {
  data: DeepPartialSkipArrayKey<FulfillRfidBadgeOrderModel>;
  children: React.ReactNode;
}

export const FulfillRfidBadgeOrderContextProvider = ({
  data,
  children,
}: FulfillRfidBadgeOrderContextProviderProps): React.JSX.Element => (
  <FulfillRfidBadgeOrderContext.Provider value={data}>
    {children}
  </FulfillRfidBadgeOrderContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoRfidBadgeOrderListContextProps {
  getPaginatedQueryParameters: GetRfidBadgeOrdersPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetRfidBadgeOrdersPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: EmspBoRfidBadgeOrderListOptionsModel;
  setColumnOptions: Dispatch<
    SetStateAction<EmspBoRfidBadgeOrderListOptionsModel>
  >;
}

export const EmspBoRfidBadgeOrderListContext =
  createContext<EmspBoRfidBadgeOrderListContextProps | null>(null);

export const useEmspBoRfidBadgeOrderListContext =
  (): EmspBoRfidBadgeOrderListContextProps => {
    const context = useContext(EmspBoRfidBadgeOrderListContext);

    if (!context) {
      throw new Error(
        'useEmspBoRfidBadgeOrderListContext must be used within EmspBoRfidBadgeOrderListContext.Provider',
      );
    }

    return context;
  };

interface EmspBoRfidBadgeOrderListContextProviderProps {
  children: React.ReactNode;
}

export const EmspBoRfidBadgeOrderListContextProvider = ({
  children,
}: EmspBoRfidBadgeOrderListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetRfidBadgeOrdersPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        status: undefined,
        textToSearch: '',
        // Sortable fields
        sortField: EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_FIELD,
        sortOrder: EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER_NAME_LIST,
        'textToSearch',
      ],
      EMSP_BO_RFID_BADGE_ORDER_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<EmspBoRfidBadgeOrderListOptionsModel>(
      EMSP_BO_RFID_BADGE_ORDER_LIST_DEFAULT_OPTIONS,
      EMSP_BO_RFID_BADGE_ORDER_LIST_OPTIONS_LSK,
    );

  const data = useMemo(
    () => ({
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    }),
    [
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      columnOptions,
      setColumnOptions,
    ],
  );

  return (
    <EmspBoRfidBadgeOrderListContext.Provider value={data}>
      {children}
    </EmspBoRfidBadgeOrderListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterEmspBoRfidBadgeOrderListContext =
  createContext<DeepPartialSkipArrayKey<GetRfidBadgeOrdersPaginatedModel> | null>(
    null,
  );

export const useFilterEmspBoRfidBadgeOrderListContext =
  (): DeepPartialSkipArrayKey<GetRfidBadgeOrdersPaginatedModel> => {
    const context = useContext(FilterEmspBoRfidBadgeOrderListContext);

    if (!context) {
      throw new Error(
        'useFilterEmspBoRfidBadgeOrderListContext must be used within FilterEmspBoRfidBadgeOrderListContext.Provider',
      );
    }

    return context;
  };

interface FilterEmspBoRfidBadgeOrderListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetRfidBadgeOrdersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterEmspBoRfidBadgeOrderListContextProvider = ({
  data,
  children,
}: FilterEmspBoRfidBadgeOrderListContextProviderProps): React.JSX.Element => {
  return (
    <FilterEmspBoRfidBadgeOrderListContext.Provider value={data}>
      {children}
    </FilterEmspBoRfidBadgeOrderListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchEmspBoRfidBadgeOrderListContext =
  createContext<DeepPartialSkipArrayKey<GetRfidBadgeOrdersPaginatedModel> | null>(
    null,
  );

export const useFilterSearchEmspBoRfidBadgeOrderListContext =
  (): DeepPartialSkipArrayKey<GetRfidBadgeOrdersPaginatedModel> => {
    const context = useContext(FilterSearchEmspBoRfidBadgeOrderListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchEmspBoRfidBadgeOrderListContext must be used within FilterSearchEmspBoRfidBadgeOrderListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchEmspBoRfidBadgeOrderListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetRfidBadgeOrdersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchEmspBoRfidBadgeOrderListContextProvider = ({
  data,
  children,
}: FilterSearchEmspBoRfidBadgeOrderListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchEmspBoRfidBadgeOrderListContext.Provider value={data}>
      {children}
    </FilterSearchEmspBoRfidBadgeOrderListContext.Provider>
  );
};
